import { useState } from "react";
import { motion } from "framer-motion";
import api, { formatError } from "../lib/api";
import { useLanguage } from "../context/LanguageContext";
import { Button } from "./ui/button";
import { toast } from "sonner";
import { BookOpen, Lock, Check, ShieldCheck } from "lucide-react";

export default function WikiPaywall() {
  const { t } = useLanguage();
  const [loading, setLoading] = useState(false);

  const startCheckout = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/payments/checkout", { origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail));
      setLoading(false);
    }
  };

  const features = ["wiki.paywall.feature1", "wiki.paywall.feature2", "wiki.paywall.feature3"];

  return (
    <div className="max-w-2xl mx-auto px-6 py-16" data-testid="wiki-paywall">
      <motion.div
        initial={{ opacity: 0, y: 18 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35 }}
        className="rounded-3xl border border-slate-200 bg-white overflow-hidden shadow-sm"
      >
        <div className="p-8 sm:p-10 bg-gradient-to-br from-teal-50 to-white border-b border-slate-100">
          <div className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-teal-700">
            <Lock size={14} /> {t("wiki.paywall.badge")}
          </div>
          <h1 className="mt-4 font-serif font-bold text-3xl sm:text-4xl tracking-tight text-slate-900">
            {t("wiki.paywall.title")}
          </h1>
          <p className="mt-3 text-base text-slate-600 leading-relaxed">{t("wiki.paywall.subtitle")}</p>
        </div>

        <div className="p-8 sm:p-10">
          <div className="flex items-baseline gap-2">
            <span className="font-serif font-bold text-5xl text-slate-900">{t("wiki.paywall.price")}</span>
            <span className="font-mono text-xs uppercase tracking-widest text-slate-400">{t("wiki.paywall.priceNote")}</span>
          </div>

          <ul className="mt-6 space-y-3">
            {features.map((f) => (
              <li key={f} className="flex items-center gap-3 text-slate-700">
                <span className="grid place-items-center w-6 h-6 rounded-full bg-teal-100 text-teal-700 shrink-0">
                  <Check size={14} />
                </span>
                <span className="text-[15px]">{t(f)}</span>
              </li>
            ))}
          </ul>

          <Button
            onClick={startCheckout}
            disabled={loading}
            data-testid="wiki-unlock-btn"
            className="mt-8 w-full h-12 bg-slate-900 hover:bg-slate-800 text-white rounded-full text-base"
          >
            <BookOpen size={18} className="mr-2" />
            {loading ? t("wiki.paywall.processing") : t("wiki.paywall.cta")}
          </Button>
          <p className="mt-4 flex items-center justify-center gap-1.5 text-xs text-slate-400">
            <ShieldCheck size={13} /> {t("wiki.paywall.secure")}
          </p>
        </div>
      </motion.div>
    </div>
  );
}
